/**
 * Free-text search over posted assembly orders.
 *
 * The PAO list resolves technician names and component lines client-side, so
 * none of this is filterable server-side without a join — the whole result set
 * is already in memory by the time it is searched.
 *
 * Component SKU and description are part of the haystack so that searching a
 * part answers "which builds used this?". An order matched only by one of its
 * components is tagged with the lines responsible; the list uses that tag to
 * open the row on the hit, which is the difference between an answer and an
 * unexplained list of orders.
 */

/** The fields a PAO row must expose to be searchable. */
export interface PaoSearchable {
  aso: string;
  build_sku: string;
  build_description: string;
  technician_name: string;
  serial_number: string | null;
  serial_numbers: Record<string, string> | null;
  to_number: string | null;
  components: { part_number: string; description: string }[];
}

/** Both sides are normalised here so no caller has to remember to do it. */
function contains(haystack: string | null | undefined, needle: string): boolean {
  return (haystack || '').toLowerCase().includes(needle.trim().toLowerCase());
}

/**
 * Part numbers on this order whose SKU or description matches the query.
 * Duplicates are collapsed: a BOM can list the same SKU on more than one line
 * (two sticks of RAM, two drives) and the badge should name it once.
 */
export function matchingComponentSkus(order: PaoSearchable, query: string): string[] {
  const seen = new Set<string>();
  const matched: string[] = [];
  for (const c of order.components) {
    if (!c.part_number) continue;
    if (!contains(c.part_number, query) && !contains(c.description, query)) continue;
    const key = c.part_number.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    matched.push(c.part_number);
  }
  return matched;
}

/** Does the order match on one of its own fields, ignoring its components? */
export function matchesOrderFields(order: PaoSearchable, query: string): boolean {
  return (
    contains(order.aso, query) ||
    contains(order.build_sku, query) ||
    contains(order.technician_name, query) ||
    contains(order.build_description, query) ||
    contains(order.serial_number, query) ||
    contains(order.to_number, query) ||
    Object.values(order.serial_numbers || {}).some(sn => contains(sn, query))
  );
}

/**
 * Filter PAOs by `search`, tagging each result with the component lines that
 * matched. An order found by its own number or SKU carries an empty tag even if
 * it also happens to contain a matching part — the tag means "this is why you
 * are seeing this row", and the row's own fields already say that.
 *
 * An empty or whitespace-only search returns the input untouched.
 */
export function filterPaosBySearch<T extends PaoSearchable>(
  orders: T[],
  search: string,
): (T & { matched_components: string[] })[] {
  const query = search.trim();
  if (!query) return orders.map(o => ({ ...o, matched_components: [] }));

  const results: (T & { matched_components: string[] })[] = [];
  for (const order of orders) {
    if (matchesOrderFields(order, query)) {
      results.push({ ...order, matched_components: [] });
      continue;
    }
    const matched = matchingComponentSkus(order, query);
    if (matched.length) results.push({ ...order, matched_components: matched });
  }
  return results;
}
